import clsx from "clsx";
import React, { useEffect, useRef, useState } from "react";
import BackButton from "web/react/components/buttons/back-button/back-button";
import { useDesktopBreakpoint } from "web/react/hooks/use-breakpoint/use-breakpoint";
import { gettext } from "web/script/modules/django-i18n";
import styles from "./drawer.module.css";

function useSmoothClosing(open: boolean): [boolean, boolean] {
    // We need to delay the open prop by a frame so the isClosing state can
    // catch up with it and let the animations work their magic
    const [isOpen, setIsOpen] = useState(open);
    const [isClosing, setIsClosing] = useState(false);

    useEffect(() => {
        if (open) {
            setIsClosing(false);
            setIsOpen(true);
        } else if (isOpen) {
            setIsOpen(false);
            setIsClosing(true);

            setTimeout(() => setIsClosing(false), 400);
        }
    }, [isOpen, open]);

    return [isOpen, isClosing];
}

interface DrawerProps {
    open: boolean;
    title: string;
    children?: JSX.Element;
    id: string;
    onBack: () => void;
    onCancel: () => void;
    onOverlay: () => void;
}

function Drawer({
    open,
    title,
    children,
    id,
    onBack,
    onCancel,
    onOverlay,
}: DrawerProps): JSX.Element {
    let overlayRef = useRef<HTMLDivElement>(null);
    let largeLayout = useDesktopBreakpoint();
    const [isOpen, isClosing] = useSmoothClosing(open);
    const [key, setKey] = useState<number>();

    useEffect(() => {
        if (isOpen) {
            document.body.classList.add("no-scroll");
        }
        return (): void => {
            document.body.classList.remove("no-scroll");
        };
    }, [isOpen]);

    useEffect(() => {
        setKey(Date.now());
    }, []);

    function handleOverlayClick(event: React.MouseEvent<HTMLDivElement>): void {
        if (event.target === overlayRef.current) {
            onOverlay();
        }
    }

    return (
        <div
            ref={overlayRef}
            onClick={handleOverlayClick}
            className={clsx(styles.overlay, {
                [styles.open]: isOpen,
                [styles.closing]: isClosing,
            })}
        >
            <article
                key={key}
                className={styles.drawer}
                role={largeLayout ? "complementary" : "dialog"}
                aria-modal={!largeLayout}
                aria-labelledby={`${id}-drawer-title`}
                aria-describedby={`${id}-drawer-children`}
                aria-hidden={!open && !largeLayout}
            >
                <div className={styles.header}>
                    <div className={styles.back}>
                        <BackButton onClick={onBack} />
                    </div>
                    <div className={styles.title} id={`${id}-drawer-title`}>
                        {title}
                    </div>
                    <button className={styles.cancel} onClick={onCancel}>
                        {gettext("feed.filters.mobile_cancel_btn_text")}
                    </button>
                </div>
                <div className={styles.content} id={`${id}-drawer-children`}>
                    {children}
                </div>
            </article>
        </div>
    );
}

export default Drawer;
